import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { auth } from "../firebase";
import VerificationModal from "./VerificationModal";

const API_BASE = process.env.REACT_APP_PAYMENT_API_BASE || "http://localhost:5001/api";

const PLAN_LABELS = { monthly: "Monthly", quarterly: "Quarterly", annual: "Annual" };

const CATEGORIES = [
  { id: "student", icon: "🎓", title: "Student" },
  { id: "senior", icon: "🧓", title: "Senior Citizen" },
];

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-IN", { dateStyle: "medium" });
}

// Plan name and the "valid till" line, from GET /payment/status.
function describePlan(info) {
  const trial = info?.trial;
  if (info?.isPremium) {
    const date = fmtDate(info.expiresAt);
    return {
      plan: PLAN_LABELS[info.plan] || "JB+ Premium",
      premium: true,
      dateLabel: "Premium active until",
      dateValue: date || "No end date",
    };
  }
  if (info?.status === "expired") {
    return {
      plan: "Free",
      premium: false,
      dateLabel: "Premium ended on",
      dateValue: fmtDate(info.expiresAt) || "Expired",
    };
  }
  if (trial?.active) {
    return {
      plan: "Free",
      premium: false,
      dateLabel: "Free trial ends on",
      dateValue: `${fmtDate(trial.endsAt)} (${trial.daysLeft} ${trial.daysLeft === 1 ? "day" : "days"} left)`,
    };
  }
  return { plan: "Free", premium: false, dateLabel: "Premium", dateValue: "Not active" };
}

function VerificationRow({ category, verification, onApply }) {
  const status = verification?.status || "none";
  const canApply = status === "none" || status === "rejected" || status === "expired";

  let badge;
  let detail = "";
  if (status === "approved") {
    badge = <span className="acct-badge acct-badge--ok">Verified</span>;
    if (verification.validUntil) detail = `Valid until ${fmtDate(verification.validUntil)}`;
  } else if (status === "pending") {
    badge = <span className="acct-badge acct-badge--wait">Pending</span>;
    detail = "Under review by our team";
  } else if (status === "rejected") {
    badge = <span className="acct-badge acct-badge--bad">Not approved</span>;
    detail = verification.rejectReason || "Please upload your document again";
  } else if (status === "expired") {
    badge = <span className="acct-badge acct-badge--bad">Expired</span>;
    detail = "Upload a current document to renew";
  } else {
    badge = <span className="acct-badge">Not applied</span>;
  }

  return (
    <div className="acct-row">
      <div className="acct-row-main">
        <div className="acct-row-title">
          <span aria-hidden="true">{category.icon}</span> {category.title}
        </div>
        {detail && <div className="acct-row-detail">{detail}</div>}
      </div>
      <div className="acct-row-side">
        {badge}
        {canApply && (
          <button type="button" className="acct-apply" onClick={() => onApply(category.id)}>
            {status === "none" ? "Apply" : "Apply again"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AccountMenu({ initials, label, onVerificationSubmitted }) {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [verifications, setVerifications] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [verifyCategory, setVerifyCategory] = useState(null);
  const wrapRef = useRef(null);

  const load = useCallback(async (isCancelled = () => false) => {
    const current = auth.currentUser;
    if (!current) return;
    setLoading(true);
    setError("");
    try {
      const token = await current.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [status, verification] = await Promise.all([
        axios.get(`${API_BASE}/payment/status`, { headers }).catch(() => null),
        axios.get(`${API_BASE}/payment/verification`, { headers }).catch(() => null),
      ]);
      if (isCancelled()) return;
      if (!status && !verification) {
        setError("Could not load your account details. Please try again.");
      } else {
        // Keep the last good value for a half-failed refresh.
        if (status) setInfo(status.data);
        if (verification) setVerifications(verification.data?.verifications || null);
      }
    } catch {
      if (!isCancelled()) setError("Could not load your account details. Please try again.");
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, []);

  // Refresh every time the popup opens, so a payment or an admin decision
  // made since the last look is always reflected.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    load(() => cancelled);
    return () => { cancelled = true; };
  }, [open, load]);

  // Close on outside click and Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const summary = describePlan(info);

  return (
    <div className="account-menu" ref={wrapRef}>
      <button
        type="button"
        className="user-pill user-pill--button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="user-avatar">{initials}</span>
        <span className="user-name">{label}</span>
      </button>

      {open && (
        <div className="account-popover" role="dialog" aria-label="Your plan">
          <div className="acct-section-label">Your plan</div>

          {!info && loading ? (
            <div className="acct-loading"><span className="spinner" /></div>
          ) : !info && error ? (
            <div className="acct-error">
              {error}{" "}
              <button type="button" className="acct-link" onClick={() => load()}>Retry</button>
            </div>
          ) : (
            <>
              <div className="acct-plan">
                <span className="acct-plan-name">{summary.plan}</span>
                <span className={`acct-badge ${summary.premium ? "acct-badge--gold" : ""}`}>
                  {summary.premium ? "JB+ active" : "Free"}
                </span>
              </div>
              <div className="acct-date">
                <span className="acct-date-label">{summary.dateLabel}</span>
                <span className="acct-date-value">{summary.dateValue}</span>
              </div>
            </>
          )}

          <div className="acct-section-label acct-section-label--gap">Student and Senior Citizen</div>
          {CATEGORIES.map((c) => (
            <VerificationRow
              key={c.id}
              category={c}
              verification={verifications?.[c.id]}
              onApply={(id) => { setOpen(false); setVerifyCategory(id); }}
            />
          ))}
          {verifications === null && !loading && error && (
            <div className="acct-error">Verification status unavailable.</div>
          )}
        </div>
      )}

      {/* Portal: the navbar's backdrop-filter would otherwise trap this
          fixed-position modal inside the 60px bar. */}
      {verifyCategory &&
        createPortal(
          <VerificationModal
            category={verifyCategory}
            onClose={() => setVerifyCategory(null)}
            onSubmitted={() => {
              setVerifyCategory(null);
              if (onVerificationSubmitted) onVerificationSubmitted();
            }}
          />,
          document.body
        )}
    </div>
  );
}
